/**
 * JsonSchemaForm — unified JSON-Schema form renderer for both ora and the
 * Outlook plugin. Supports the union of features the two surfaces need:
 *
 * Field types:
 *   - ``string``  → ``<input type="text">``
 *   - ``string`` with ``enum`` → ``<select>``
 *   - ``boolean`` → checkbox
 *   - ``number`` / ``integer`` → ``<input type="number">``
 *   - ``multiline`` → ``<textarea>`` (substrate ui-hint extension)
 *   - ``array_of_objects`` → blank-line-separated textarea
 *   - anything else → JSON-snippet textarea fallback
 *
 * State modes (mutually exclusive — discriminator is whether ``value`` is
 * defined):
 *
 *   - **Controlled** (plugin pattern): caller supplies ``value`` + ``onChange``.
 *     Every keystroke flows up; the parent owns state and can persist /
 *     compare / pre-fill from analyze_email. Used by the addin's HITL
 *     drawer, launch tab, and pre-launch dialog.
 *
 *   - **Uncontrolled** (ora pattern): caller supplies ``initialValues`` +
 *     ``onSubmit``. The form keeps its own internal state and surfaces
 *     values only when the user clicks Submit. Used by ora's drawer and
 *     ``WorkflowReviewDeck``.
 *
 * Plugin-only opt-ins:
 *
 *   - ``fieldConfidence`` — analyze_email confidence chip + coloured left
 *     border per field. Hidden when undefined.
 *   - ``fieldLoading`` — spinner alongside the field label while a prefill
 *     value is in flight. Hidden when undefined.
 *
 * Read-only mode disables every input and hides Submit / Cancel — used
 * when rendering past HITL responses inside the audit-trail card.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Collapse } from "@mui/material";
import type { FieldSchema } from "../schemaHelpers";
import {
  checkAllRequiredFilled,
  parseObjectSchema,
  titleCaseKey,
} from "../schemaHelpers";
import { classifySensitivity } from "../sensitivity";
import { OptionIndicator, OptionRow } from "~/common/components/OptionRow";
import {
  SchemaFileUploadField,
  type SchemaFileUploadValue,
} from "./SchemaFileUploadField";
import { CONFIDENCE_COLORS, type ConfidenceLevel } from "./confidenceChip";

// ---------------------------------------------------------------------------
// x-widget convention
// ---------------------------------------------------------------------------
//
// Schema fields can opt into a non-default widget via the ``x-widget``
// JSON-Schema extension. Currently supported values:
//
//   * ``"file_upload"`` — drag-and-drop file picker; emits an array of
//     ``{name, mime_type, bytes_b64}`` shapes as the field value.
//     First user: ``test_draft_reply_email_setup``'s ``launch_attachments``.
//
// Document new values in ``docs/superpowers/workflow-conventions.md`` when
// you add them so the convention stays discoverable and doesn't atrophy
// into a tangle of x-render / x-control / x-control-type aliases.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// Defined in ./confidenceChip so the decision surface renders the SAME chip
// from the SAME palette. Re-exported here because many modules already import
// the type from this file.
export type { ConfidenceLevel } from "./confidenceChip";

// `border` is an inline style value, so it carries the hex of the same
// Highstreet token named in `text`/`dot`. This component renders in both ora and
// the Outlook add-in, so it cannot import either app's token module — keep the
// hex and the token name together when editing.

export interface JsonSchemaFormProps {
  schema: Record<string, unknown> | null | undefined;

  // Controlled-mode props (plugin):
  value?: Record<string, unknown>;
  onChange?: (next: Record<string, unknown>) => void;

  // Uncontrolled-mode props (ora):
  initialValues?: Record<string, unknown>;
  onSubmit?: (values: Record<string, unknown>) => void;

  // Shared:
  onCancel?: () => void;
  submitting?: boolean;
  /**
   * Non-null = Submit is disabled and this string explains WHY (rendered as the
   * button's title).
   *
   * Distinct from `submitting` on purpose. Callers used to express "not allowed
   * to launch yet" by folding the condition into `submitting`, which disables
   * every field in the form (including the control the operator needs to CLEAR
   * the block) and renders the button as a spinner labelled "Submitting…" —
   * i.e. it reads as "in flight" when nothing has been sent. This prop blocks
   * only the button, and says why.
   *
   * Distinct from `submitReady` too: that one owns field validity. This is for
   * preconditions OUTSIDE the payload, like "the SP pool has no members".
   */
  submitDisabledReason?: string | null;
  submitLabel?: string;
  cancelLabel?: string;
  /** Optional title rendered above the fields. */
  title?: string;
  /** When true, every field is disabled and Submit / Cancel hide. */
  readOnly?: boolean;

  // Plugin-only opt-ins:
  /** Per-field confidence (analyze_email extraction). Keyed by schema property name. */
  fieldConfidence?: Record<string, ConfidenceLevel>;
  /** When true for a key, renders a loading spinner alongside that field's label. */
  fieldLoading?: Record<string, boolean>;
  /**
   * HITL opt-in. When true, enum fields with ≤3 values render as pill buttons
   * instead of a ``<select>`` dropdown. Clicking a pill sets the value AND
   * submits the form if every other required field is satisfied. The field's
   * ``x-destructive`` value (if declared) shows an inline confirmation strip
   * before submit. Mixed schemas keep their non-enum fields unchanged.
   */
  submitOnPillSelect?: boolean;
  /**
   * Per-field custom renderer, keyed by schema property name. When present
   * for a field, the override renders the ENTIRE field row (label included)
   * in place of the default widget — used e.g. to swap a free-text input for
   * a constrained combobox whose options arrive at runtime. The override
   * must write changes back through ``setValue(key, next)``.
   */
  fieldOverrides?: Record<
    string,
    (args: {
      field: FieldSchema;
      value: unknown;
      setValue: (key: string, next: unknown) => void;
      readOnly: boolean;
    }) => React.ReactNode
  >;
  /**
   * Like ``fieldOverrides``, but for SUB-fields inside an
   * ``array_of_objects`` row. Keyed ``"<arrayKey>.<subKey>"`` (e.g.
   * ``"mortgagees.mailing_state"``); the renderer replaces that sub-field's
   * row in EVERY item card (return ``null`` to suppress a row entirely —
   * used when a composite override renders a sibling key's control).
   * ``field`` is the sub-field's schema (original sub key), ``value`` the
   * item's current sub-value, and ``setValue(subKey, next)`` writes any key
   * of that row's item object. ⚠️ ONE write per event tick: the setter maps
   * over the render's item snapshot, so a second same-tick write clobbers
   * the first — drive each key from its own user event (which is how real
   * composite controls behave anyway). Opt-in — split-address mortgagee
   * form (2026-08-03) uses it for the State+ZIP line.
   */
  itemFieldOverrides?: Record<
    string,
    (args: {
      field: FieldSchema;
      value: unknown;
      /** Writes any sub-key of THIS row — pass the sub-field key explicitly.
       *  (Unlike the default renderer's bound setter, the key is honored, so
       *  one override can drive a composite control spanning two keys.) */
      setValue: (key: string, next: unknown) => void;
      readOnly: boolean;
      /** Row index within the array — for per-row DOM ids/testids. */
      index: number;
      /** The row's full current item object (read-only view). */
      item: Record<string, unknown>;
    }) => React.ReactNode
  >;
  /**
   * Per-field validation errors, keyed by schema property name. A non-empty
   * string renders as an inline error under that field AND blocks Submit
   * (the disabled-submit tooltip explains). Owned by the caller so
   * cross-field / runtime-data rules (e.g. date-within-policy-term) live
   * where the data lives.
   */
  fieldErrors?: Record<string, string | null | undefined>;
  /**
   * Per-field disable, keyed by schema property name. Disabled fields render
   * read-only — used for values that are DRIVEN by another field's selection
   * (e.g. carrier/type following a policy-number pick) so users can see but
   * not desync them.
   */
  fieldDisabled?: Record<string, boolean>;
  /**
   * Per-field HIDE, keyed by schema property name. Symmetric with
   * `fieldDisabled`, and deliberately separate from it: disabled means "you may
   * see this but not desync it", hidden means "this question does not apply".
   *
   * Added for the mortgagee change-type selector — choosing "Remove a
   * mortgagee" must stop asking for a new one. Before this, `visibleFields`
   * filtered exactly one thing (the deferred-notes field in pill mode), so
   * there was no way to hide a field at all.
   *
   * ⚠️ A hidden field STILL VALIDATES and is still submitted. Hiding is a
   * presentation concern, not a schema one — callers must keep sending a value
   * the server accepts (for `mortgagees`, an empty array: an omitted key is
   * rejected, whereas `[]` validates).
   */
  fieldHidden?: Record<string, boolean>;
  /**
   * Hide every field's schema `description` helper text. Those descriptions are
   * authored for the LLM that fills the form (they are the Pydantic field docs),
   * so a compact surface like the Outlook task pane can suppress them WITHOUT
   * editing the workflow input model — which would also degrade extraction and
   * the MCP tool docs. Opt-in: ora keeps showing descriptions.
   */
  hideFieldDescriptions?: boolean;
  /**
   * Per-field replacement helper text, shown even when `hideFieldDescriptions`
   * is set. Use for short operator-facing notes (e.g. what a blank field
   * falls back to).
   */
  fieldNotes?: Record<string, string>;
  /**
   * Presentation-only reorder, by schema property name. Listed fields render
   * first, in the given order; unlisted fields follow in their schema order.
   * The schema stays the wire/validation truth — this exists because model
   * field order is authored for the LLM/extraction docs, while a task pane
   * wants editable-first / read-only-last.
   */
  fieldOrder?: string[];
  /**
   * Visual grouping: each section's member fields render inside one bordered
   * container, placed where the section's FIRST visible member falls in the
   * field order. Members are collected wherever they appear, so keep a
   * section's fields adjacent in `fieldOrder` or the gathering will surprise
   * you. Hidden members simply don't render; an all-hidden section renders
   * nothing (no empty frame).
   */
  fieldSections?: Array<{ title?: string; fields: string[]; testId?: string }>;
  /**
   * Arbitrary content rendered after the last field and immediately BEFORE
   * the footer (submit/cancel row). For controls that belong to the launch
   * decision rather than the payload — e.g. the Epic sign-in disclosure —
   * so they sit right above the button that commits them.
   */
  preFooter?: ReactNode;
}

// `checkAllRequiredFilled` moved to `~/common/workflows/schemaHelpers` on
// 2026-08-27 (see the import at the top of this file). The reworked mortgagee
// launch form renders its per-action slice through a footer-less
// `JsonSchemaForm`, so it needs the same submit-readiness rule without the
// renderer's gate. Behaviour is unchanged — this file's own suite is the
// regression floor.

/**
 * The field list the submit-readiness check runs against: the same one
 * `ArrayOfObjectsField` actually RENDERS.
 *
 * `checkAllRequiredFilled` recurses into an `array_of_objects` field's full
 * `itemFields`, but the renderer drops any sub-field `fieldHidden` hides (on
 * either address — the bare sub key or the dotted `<arrayKey>.<subKey>` form).
 * A required sub-field hidden for the current context therefore had no control
 * to fill it in and no substrate default to satisfy it, so the check saw
 * `undefined` forever and Submit stayed disabled with no way for the AM to
 * recover.
 *
 * The renderer's view is the one source of truth: if the form does not ASK for
 * a value, it cannot demand one. Scoped deliberately to item sub-fields —
 * hiding a TOP-LEVEL field is documented (see `fieldHidden`'s prop doc) as a
 * presentation-only concern where the caller keeps supplying a value the
 * server accepts, and that contract stays exactly as it was.
 *
 * Recurses, because a sub-field can itself be an array (`mortgagees` inside
 * `actions`), and matches the renderer's key derivation at every level: a
 * nested array is addressed by its own schema key (`FieldRow` passes
 * `overrideKey={sub.key}`), never by a dotted path through its parent.
 */
function fieldsForRequiredCheck(
  fields: FieldSchema[],
  fieldHidden: Record<string, boolean> | undefined,
): FieldSchema[] {
  if (!fieldHidden) return fields;
  let changed = false;
  const out = fields.map((f) => {
    if (f.type !== "array_of_objects" || !f.itemFields) return f;
    const visible = f.itemFields.filter(
      (sub) => !fieldHidden[sub.key] && !fieldHidden[`${f.key}.${sub.key}`],
    );
    const itemFields = fieldsForRequiredCheck(visible, fieldHidden);
    if (
      itemFields.length === f.itemFields.length &&
      itemFields.every((sub, i) => sub === f.itemFields![i])
    ) {
      return f;
    }
    changed = true;
    return { ...f, itemFields };
  });
  return changed ? out : fields;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function JsonSchemaForm({
  schema,
  value: controlledValue,
  onChange,
  initialValues = {},
  onSubmit,
  onCancel,
  submitting = false,
  submitDisabledReason = null,
  submitLabel = "Submit",
  cancelLabel = "Cancel",
  title,
  readOnly = false,
  fieldConfidence,
  fieldLoading,
  submitOnPillSelect = false,
  fieldOverrides,
  itemFieldOverrides,
  fieldErrors,
  fieldDisabled,
  fieldHidden,
  hideFieldDescriptions = false,
  fieldNotes,
  fieldOrder,
  fieldSections,
  preFooter,
}: JsonSchemaFormProps) {
  const fields = useMemo(() => parseObjectSchema(schema ?? null), [schema]);

  // Controlled when ``value`` is defined; uncontrolled otherwise. Parallels
  // React's <input> dual-mode contract.
  const isControlled = controlledValue !== undefined;
  const [internalValues, setInternalValues] = useState<Record<string, unknown>>(
    initialValues,
  );
  const values = isControlled ? controlledValue! : internalValues;

  // JSON-fallback textarea state (only used when the schema isn't a
  // recognised ``type: "object"`` shape).
  const [jsonText, setJsonText] = useState<string>(() =>
    Object.keys(values).length > 0 ? JSON.stringify(values, null, 2) : "{}",
  );
  const [jsonError, setJsonError] = useState<string | null>(null);

  const setField = useCallback(
    (key: string, next: unknown) => {
      if (isControlled) {
        onChange?.({ ...values, [key]: next });
      } else {
        setInternalValues((prev) => ({ ...prev, [key]: next }));
      }
    },
    [isControlled, onChange, values],
  );

  const handleJsonChange = useCallback(
    (text: string) => {
      setJsonText(text);
      try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          setJsonError(null);
          if (isControlled) onChange?.(parsed as Record<string, unknown>);
          else setInternalValues(parsed as Record<string, unknown>);
        } else {
          setJsonError("Top-level value must be an object.");
        }
      } catch (err) {
        setJsonError(`Invalid JSON: ${(err as Error).message}`);
      }
    },
    [isControlled, onChange],
  );

  // Any non-empty caller-supplied field error blocks Submit alongside the
  // required-fields check.
  const hasFieldErrors = useMemo(
    () => !!fieldErrors && Object.values(fieldErrors).some((e) => !!e),
    [fieldErrors],
  );

  // The renderer's own view of what an array item asks for — never the raw
  // `itemFields`. See `fieldsForRequiredCheck`.
  const requiredCheckFields = useMemo(
    () => (fields ? fieldsForRequiredCheck(fields, fieldHidden) : fields),
    [fields, fieldHidden],
  );

  // Submit-ready when every required field has a non-empty value AND no
  // field-level validation errors. In the JSON fallback path, ready when
  // JSON parses without error.
  const submitReady = useMemo(() => {
    if (!requiredCheckFields) return jsonError === null;
    return (
      checkAllRequiredFilled(requiredCheckFields, values) && !hasFieldErrors
    );
  }, [requiredCheckFields, values, jsonError, hasFieldErrors]);

  /** Submit with an inline field override — used by pill buttons so the
   *  click handler can submit using the value it just set, without waiting
   *  for the next React render to flow through ``values``. */
  const submitWith = useCallback(
    (overrides: Record<string, unknown>) => {
      if (!onSubmit) return;
      onSubmit({ ...values, ...overrides });
    },
    [onSubmit, values],
  );

  /** Same readiness check as ``submitReady`` but with one-or-more field
   *  values overridden — lets a pill click ask "would the form be ready if
   *  I picked this option?" before committing to a submit. */
  const computeReadyWith = useCallback(
    (overrides: Record<string, unknown>) => {
      if (!requiredCheckFields) return false;
      return checkAllRequiredFilled(requiredCheckFields, {
        ...values,
        ...overrides,
      });
    },
    [requiredCheckFields, values],
  );

  const handleFormSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (readOnly || !submitReady) return;
      if (!onSubmit) return;
      onSubmit(values);
    },
    [readOnly, submitReady, onSubmit, values],
  );

  // When pills will be rendered they ARE the submit action, so the Submit
  // button becomes redundant and visually competes with them. Hide it.
  const hasPillField = useMemo(
    () =>
      !!fields &&
      submitOnPillSelect &&
      !readOnly &&
      fields.some(
        (f) =>
          f.type === "enum" &&
          (f.enumValues?.length ?? 0) > 0 &&
          (f.enumValues?.length ?? 0) <= 3,
      ),
    [fields, submitOnPillSelect, readOnly],
  );

  // When there's a destructive pill enum AND an optional notes-style field,
  // defer the notes field into the destructive confirmation strip so users
  // get a chance to explain what was wrong. Keys recognised: `notes`, `note`.
  const deferredNotesField = useMemo(() => {
    if (!hasPillField || !fields) return null;
    const hasDestructiveEnum = fields.some(
      (f) => f.type === "enum" && !!f.xDestructive,
    );
    if (!hasDestructiveEnum) return null;
    return (
      fields.find(
        (f) =>
          !f.required &&
          (f.key === "notes" || f.key === "note") &&
          (f.type === "string" || f.type === "multiline"),
      ) ?? null
    );
  }, [fields, hasPillField]);

  const visibleFields = useMemo(() => {
    if (!fields) return fields;
    // COMPOSE, never replace: pill-mode HITL forms already depend on the
    // deferred-notes field being filtered out here. fieldHidden is an
    // additional filter on top of that, not an alternative to it.
    let out = fields;
    if (deferredNotesField) {
      out = out.filter((f) => f.key !== deferredNotesField.key);
    }
    if (fieldHidden) {
      out = out.filter((f) => !fieldHidden[f.key]);
    }
    if (fieldOrder && fieldOrder.length > 0) {
      // Listed keys first in the given order; unlisted fields keep their
      // schema order after them (Array.prototype.sort is stable).
      const pos = new Map(fieldOrder.map((k, i) => [k, i]));
      out = [...out].sort(
        (a, b) =>
          (pos.get(a.key) ?? Number.MAX_SAFE_INTEGER) -
          (pos.get(b.key) ?? Number.MAX_SAFE_INTEGER),
      );
    }
    return out;
  }, [fields, deferredNotesField, fieldHidden, fieldOrder]);

  const showFooter = !readOnly && ((onSubmit && !hasPillField) || onCancel);
  const showSubmitButton = !!onSubmit && !hasPillField;

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={handleFormSubmit}
      data-testid="json-schema-form"
    >
      {title && (
        <h2 className="text-display-2xs font-bold text-primary-gray-light-900">
          {title}
        </h2>
      )}

      {fields === null ? (
        <FallbackJson
          text={jsonText}
          onChange={handleJsonChange}
          error={jsonError}
          disabled={readOnly}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {fields.length === 0 && (
            <p className="text-sm text-primary-gray-light-600">
              No fields required for this step.
            </p>
          )}
          {(() => {
            const ordered = visibleFields ?? fields;
            const renderFieldNode = (f: (typeof ordered)[number]) => {
              const override = fieldOverrides?.[f.key];
              const error = fieldErrors?.[f.key];
              return (
                <div key={f.key} className="flex flex-col gap-1">
                  {override ? (
                    override({
                      field: f,
                      value: values[f.key],
                      setValue: setField,
                      readOnly: readOnly || !!fieldDisabled?.[f.key],
                    })
                  ) : (
                    <FieldRow
                      field={f}
                      value={values[f.key]}
                      setValue={setField}
                      confidence={fieldConfidence?.[f.key]}
                      loading={fieldLoading?.[f.key]}
                      readOnly={readOnly || !!fieldDisabled?.[f.key]}
                      submitOnPillSelect={submitOnPillSelect}
                      submitWith={submitWith}
                      computeReadyWith={computeReadyWith}
                      submitting={submitting}
                      hideDescription={hideFieldDescriptions}
                      note={fieldNotes?.[f.key]}
                      itemFieldOverrides={itemFieldOverrides}
                      fieldConfidence={fieldConfidence}
                      fieldLoading={fieldLoading}
                      fieldHidden={fieldHidden}
                      fieldErrors={fieldErrors}
                      fieldNotes={fieldNotes}
                      deferredNotesField={
                        f.type === "enum" && f.xDestructive && deferredNotesField
                          ? {
                              field: deferredNotesField,
                              value: values[deferredNotesField.key],
                              setValue: setField,
                            }
                          : undefined
                      }
                    />
                  )}
                  {error && (
                    <span
                      className="text-xs text-primary-error-700"
                      role="alert"
                      data-testid={`field-error-${f.key}`}
                    >
                      {error}
                    </span>
                  )}
                </div>
              );
            };
            if (!fieldSections || fieldSections.length === 0) {
              return ordered.map(renderFieldNode);
            }
            // Section-aware pass: a section renders once, at the position of
            // its first visible member, containing every visible member.
            const sectionFor = (key: string) =>
              fieldSections.find((s) => s.fields.includes(key));
            const emitted = new Set<(typeof fieldSections)[number]>();
            return ordered.map((f) => {
              const section = sectionFor(f.key);
              if (!section) return renderFieldNode(f);
              if (emitted.has(section)) return null;
              emitted.add(section);
              const members = ordered.filter((m) =>
                section.fields.includes(m.key),
              );
              return (
                <div
                  key={`section-${section.fields[0]}`}
                  className="flex flex-col gap-3 rounded-lg border border-primary-gray-light-300 p-3"
                  data-testid={section.testId}
                >
                  {section.title && (
                    <div className="text-xs font-bold uppercase tracking-wide text-primary-gray-light-700">
                      {section.title}
                    </div>
                  )}
                  {members.map(renderFieldNode)}
                </div>
              );
            });
          })()}
        </div>
      )}

      {preFooter}

      {showFooter && (
        <div className="w-full flex justify-end gap-2 pt-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="btn-tertiary-gray text-sm px-3 py-1.5 disabled:opacity-50"
            >
              {cancelLabel}
            </button>
          )}
          {showSubmitButton && (
            <button
              type="submit"
              disabled={submitting || !submitReady || !!submitDisabledReason}
              // When greyed out, explain why (R7): missing required fields
              // vs. field-level validation errors vs. an external precondition.
              // The external reason wins — a full, valid form that still cannot
              // launch is the most confusing state, so name that cause first.
              title={
                submitDisabledReason
                  ? submitDisabledReason
                  : !submitReady
                    ? hasFieldErrors
                      ? "Fix the highlighted field errors before submitting."
                      : "Fill all required fields before submitting."
                    : undefined
              }
              className="btn-primary text-sm px-4 py-2 disabled:opacity-50"
            >
              {submitting ? (
                <span className="flex items-center gap-1.5">
                  <span
                    aria-hidden="true"
                    className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
                  />
                  Submitting…
                </span>
              ) : (
                submitLabel
              )}
            </button>
          )}
        </div>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Field renderers
// ---------------------------------------------------------------------------

function FieldRow({
  field,
  value,
  setValue,
  confidence,
  loading,
  readOnly = false,
  submitOnPillSelect = false,
  submitWith,
  computeReadyWith,
  submitting = false,
  deferredNotesField,
  hideDescription = false,
  note,
  itemFieldOverrides,
  overrideKey,
  fieldConfidence,
  fieldLoading,
  fieldHidden,
  fieldErrors,
  fieldNotes,
}: {
  field: FieldSchema;
  value: unknown;
  setValue: (key: string, next: unknown) => void;
  confidence?: ConfidenceLevel;
  loading?: boolean;
  readOnly?: boolean;
  /**
   * The STABLE schema key this field is addressed by in the caller's per-field
   * maps (`itemFieldOverrides`, `fieldConfidence`, …). Defaults to
   * `field.key`. `ArrayOfObjectsField` synthesizes a per-row `field.key`
   * (`"<arrayKey>-<idx>-<subKey>"`) so DOM ids don't collide across rows — that
   * synthetic key must never reach a lookup, or a nested array's own
   * `itemFieldOverrides` entries ("mortgagees.mailing_state") stop matching.
   */
  overrideKey?: string;
  submitOnPillSelect?: boolean;
  submitWith?: (overrides: Record<string, unknown>) => void;
  computeReadyWith?: (overrides: Record<string, unknown>) => boolean;
  submitting?: boolean;
  /** Sub-field custom renderers for array_of_objects rows — see the
   *  JsonSchemaFormProps doc. Only consulted when this field IS the array. */
  itemFieldOverrides?: Record<
    string,
    (args: {
      field: FieldSchema;
      value: unknown;
      /** Writes any sub-key of THIS row — pass the sub-field key explicitly.
       *  (Unlike the default renderer's bound setter, the key is honored, so
       *  one override can drive a composite control spanning two keys.) */
      setValue: (key: string, next: unknown) => void;
      readOnly: boolean;
      /** Row index within the array — for per-row DOM ids/testids. */
      index: number;
      /** The row's full current item object (read-only view). */
      item: Record<string, unknown>;
    }) => React.ReactNode
  >;
  /**
   * The FULL per-field maps, threaded through so an `array_of_objects` field
   * can decorate its SUB-fields the way the top-level loop decorates it. Only
   * consulted when this field IS the array; the scalar branches keep using the
   * already-resolved `confidence` / `loading` / `note` props above.
   */
  fieldConfidence?: Record<string, ConfidenceLevel>;
  fieldLoading?: Record<string, boolean>;
  fieldHidden?: Record<string, boolean>;
  fieldErrors?: Record<string, string | null | undefined>;
  fieldNotes?: Record<string, string>;
  /** Suppress the schema's own `description` helper text for this field. */
  hideDescription?: boolean;
  /** Replacement helper text, shown even when descriptions are hidden. */
  note?: string;
  /** When set, EnumPillField renders this field's input inside its destructive
   *  confirmation strip instead of the main form (e.g. for an optional `notes`
   *  textarea on a reject flow). */
  deferredNotesField?: {
    field: FieldSchema;
    value: unknown;
    setValue: (key: string, next: unknown) => void;
  };
}) {
  const conf = confidence ? CONFIDENCE_COLORS[confidence] : null;

  const labelEl = (
    <label
      htmlFor={`schema-${field.key}`}
      className="flex items-center gap-1.5 text-xs font-bold text-primary-gray-light-700"
    >
      {field.label}
      {field.required && <span className="text-primary-error-600 ms-1">*</span>}
      {conf && (
        <span
          className={`inline-flex items-center gap-[3px] text-2xs font-medium ${conf.text}`}
          title={`${conf.label} confidence`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${conf.dot}`} />
          {conf.label}
        </span>
      )}
      {loading && (
        <span
          className="ml-0.5 inline-block h-2.5 w-2.5 animate-spin rounded-full border border-primary-gray-light-200 border-t-primary-gray-light-500"
          role="status"
          aria-label="Loading prefill value"
        />
      )}
    </label>
  );
  // A caller-supplied `note` always wins; otherwise the schema's own
  // description shows unless the surface has opted out. Schema descriptions are
  // written for the LLM that fills these fields (they live on the Pydantic
  // model), so a surface can hide them without touching the workflow contract.
  const helperText = note ?? (hideDescription ? undefined : field.description);
  const helperEl = helperText ? (
    <span className="text-xs text-primary-gray-light-500">{helperText}</span>
  ) : null;

  const inputBorderStyle = conf
    ? { borderLeftColor: conf.border, borderLeftWidth: "3px" }
    : undefined;

  // x-widget overrides the default per-type widget. Checked first so it
  // wins regardless of the underlying schema type.
  if (field.xWidget === "file_upload") {
    const arrayValue: SchemaFileUploadValue[] = Array.isArray(value)
      ? (value as SchemaFileUploadValue[])
      : [];
    return (
      <div className="flex flex-col gap-1">
        {labelEl}
        {helperEl}
        <SchemaFileUploadField
          value={arrayValue}
          onChange={(next) => setValue(field.key, next)}
          disabled={readOnly}
        />
      </div>
    );
  }

  if (field.type === "boolean") {
    return (
      <div className="relative flex flex-row items-start gap-2">
        {/* labelEl is itself a <label htmlFor>, so this cannot use <OptionRow>
            (nested labels are invalid and double-fire the control) — it shares
            the indicator instead. `relative` keeps the sr-only input inside
            this row for the same reason OptionRow's label is `relative`. */}
        <input
          id={`schema-${field.key}`}
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => setValue(field.key, e.target.checked)}
          disabled={readOnly}
          className="peer sr-only"
        />
        <OptionIndicator checked={Boolean(value)} type="checkbox" />
        <div className="flex flex-col">
          {labelEl}
          {helperEl}
        </div>
      </div>
    );
  }

  if (field.type === "enum") {
    const opts = field.enumValues ?? [];
    // x-render="radio" opts out of pill mode and forces a radio-button
    // group. Use this for *selection* fields (e.g. "which method?") so
    // they read as picking-one rather than committing. The decision pills
    // elsewhere in the form remain the only commit affordance.
    if (field.xRender === "radio" && !readOnly && opts.length > 0) {
      return (
        <fieldset className="flex flex-col gap-2">
          <legend className="flex items-center gap-1.5 text-xs font-bold text-primary-gray-light-700">
            {field.label}
            {field.required && <span className="text-primary-error-600 ms-1">*</span>}
          </legend>
          <div
            className={
              field.xRenderDirection === "horizontal"
                ? "flex flex-row flex-wrap items-center gap-x-4 gap-y-1.5"
                : "flex flex-col gap-1.5"
            }
            data-testid={`enum-radios-${field.key}`}
          >
            {opts.map((opt, idx) => {
              const label = field.enumLabels?.[idx] ?? titleCaseKey(opt);
              const id = `schema-${field.key}-${opt}`;
              return (
                <OptionRow
                  key={opt}
                  id={id}
                  name={`schema-${field.key}`}
                  value={opt}
                  checked={String(value ?? "") === opt}
                  onChange={() => setValue(field.key, opt)}
                  inputTestId={`enum-radio-${field.key}-${opt}`}
                  className="text-sm text-primary-gray-light-900"
                >
                  {label}
                </OptionRow>
              );
            })}
          </div>
          {helperEl}
        </fieldset>
      );
    }
    // Pill-button mode: HITL caller opted in AND there are ≤3 options.
    if (
      submitOnPillSelect &&
      !readOnly &&
      opts.length > 0 &&
      opts.length <= 3 &&
      submitWith &&
      computeReadyWith
    ) {
      return (
        <EnumPillField
          field={field}
          options={opts}
          value={value}
          setValue={setValue}
          submitWith={submitWith}
          computeReadyWith={computeReadyWith}
          submitting={submitting}
          labelEl={labelEl}
          helperEl={helperEl}
          deferredNotesField={deferredNotesField}
        />
      );
    }
    // Default: <select> dropdown (4+ options, or caller didn't opt in).
    return (
      <div className="flex flex-col gap-1">
        {labelEl}
        <select
          id={`schema-${field.key}`}
          value={value === undefined || value === null ? "" : String(value)}
          onChange={(e) => setValue(field.key, e.target.value)}
          disabled={readOnly}
          className="rounded-lg border border-primary-gray-light-300 px-3 py-2 text-sm bg-base-white text-primary-gray-light-900 disabled:bg-primary-gray-light-50 disabled:text-primary-gray-light-700"
          style={inputBorderStyle}
        >
          <option value="" disabled>
            Select…
          </option>
          {opts.map((opt, idx) => (
            <option key={opt} value={opt}>
              {field.enumLabels?.[idx] ?? opt}
            </option>
          ))}
        </select>
        {helperEl}
      </div>
    );
  }

  if (field.type === "number" || field.type === "integer") {
    return (
      <div className="flex flex-col gap-1">
        {labelEl}
        <input
          id={`schema-${field.key}`}
          type="number"
          step={field.type === "integer" ? 1 : "any"}
          value={value === undefined || value === null ? "" : String(value)}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") {
              setValue(field.key, undefined);
              return;
            }
            const n =
              field.type === "integer" ? parseInt(raw, 10) : parseFloat(raw);
            setValue(field.key, Number.isNaN(n) ? raw : n);
          }}
          disabled={readOnly}
          className="rounded-lg border border-primary-gray-light-300 px-3 py-2 text-sm bg-base-white text-primary-gray-light-900 disabled:bg-primary-gray-light-50 disabled:text-primary-gray-light-700"
          style={inputBorderStyle}
        />
        {helperEl}
      </div>
    );
  }

  if (field.type === "string") {
    return (
      <div className="flex flex-col gap-1">
        {labelEl}
        <input
          id={`schema-${field.key}`}
          type={classifySensitivity({ format: field.format, "x-sensitive": field.xSensitive }, field.key) !== null ? "password" : "text"}
          value={value === undefined || value === null ? "" : String(value)}
          onChange={(e) => setValue(field.key, e.target.value)}
          disabled={readOnly}
          className="rounded-lg border border-primary-gray-light-300 px-3 py-2 text-sm bg-base-white text-primary-gray-light-900 disabled:bg-primary-gray-light-50 disabled:text-primary-gray-light-700"
          style={inputBorderStyle}
        />
        {helperEl}
      </div>
    );
  }

  if (field.type === "multiline") {
    return (
      <div className="flex flex-col gap-1">
        {labelEl}
        <textarea
          id={`schema-${field.key}`}
          value={value === undefined || value === null ? "" : String(value)}
          onChange={(e) => setValue(field.key, e.target.value)}
          rows={4}
          disabled={readOnly}
          className="rounded-lg border border-primary-gray-light-300 px-3 py-2 text-sm bg-base-white text-primary-gray-light-900 disabled:bg-primary-gray-light-50 disabled:text-primary-gray-light-700 whitespace-pre-wrap"
          style={inputBorderStyle}
        />
        {helperEl}
      </div>
    );
  }

  if (field.type === "array_of_objects") {
    return (
      <ArrayOfObjectsField
        field={field}
        value={value}
        setValue={setValue}
        readOnly={readOnly}
        inputBorderStyle={inputBorderStyle}
        labelEl={labelEl}
        helperEl={helperEl}
        itemFieldOverrides={itemFieldOverrides}
        overrideKey={overrideKey ?? field.key}
        fieldConfidence={fieldConfidence}
        fieldLoading={fieldLoading}
        fieldHidden={fieldHidden}
        fieldErrors={fieldErrors}
        fieldNotes={fieldNotes}
        hideDescription={hideDescription}
      />
    );
  }

  // Unknown / unsupported per-field type — JSON snippet textarea.
  return (
    <div className="flex flex-col gap-1">
      {labelEl}
      <textarea
        id={`schema-${field.key}`}
        value={
          value === undefined || value === null
            ? ""
            : JSON.stringify(value, null, 2)
        }
        onChange={(e) => {
          try {
            setValue(field.key, JSON.parse(e.target.value));
          } catch {
            setValue(field.key, e.target.value);
          }
        }}
        rows={3}
        disabled={readOnly}
        className="rounded-lg border border-primary-gray-light-300 px-3 py-2 text-sm bg-base-white text-primary-gray-light-900 font-mono disabled:bg-primary-gray-light-50 disabled:text-primary-gray-light-700"
        style={inputBorderStyle}
      />
      <span className="text-2xs text-primary-warning-700">
        Field type not fully supported — enter JSON directly.
      </span>
      {helperEl}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EnumPillField — pill-button rendering for ≤3-option enum fields when the
// HITL caller opts in via `submitOnPillSelect`. Matches HitlChoicePanel's
// visual hierarchy (first=primary, last=secondary-gray, middle=tertiary-gray)
// and submit-on-click flow, including inline confirmation for the value
// declared as `x-destructive` on the schema.
// ---------------------------------------------------------------------------

/** All pills get a full visible colored border. The first (sunrise-filled)
 *  pill uses the darker blossom shade so the border reads against the fill. */
function getEnumPillClass(
  option: string,
  index: number,
  destructiveValue: string | null,
): string {
  const base =
    "flex-1 text-sm rounded-lg border-[1px] border-solid px-[0.75rem] py-[0.5rem] font-bold transition-shadow";
  if (option === destructiveValue) return `${base} btn-error-secondary`;
  if (index === 0) return `${base} btn-primary !border-blossom`;
  return `${base} btn-secondary-gray`;
}

/** Resolve an enum option's display label.
 *
 * Prefers ``field.enumLabels[i]`` (sourced from the schema's ``x-enum-titles``
 * — lets the substrate keep stable enum keys while surfacing AM-friendly
 * labels) and falls back to ``titleCaseKey(option)`` so existing schemas
 * without the annotation render unchanged.
 */
function getEnumOptionLabel(
  field: FieldSchema,
  options: string[],
  option: string,
): string {
  const idx = options.indexOf(option);
  if (idx >= 0) {
    const label = field.enumLabels?.[idx];
    if (typeof label === "string" && label.length > 0) {
      return label;
    }
  }
  return titleCaseKey(option);
}

function EnumPillField({
  field,
  options,
  value,
  setValue,
  submitWith,
  computeReadyWith,
  submitting,
  labelEl,
  helperEl,
  deferredNotesField,
}: {
  field: FieldSchema;
  options: string[];
  value: unknown;
  setValue: (key: string, next: unknown) => void;
  submitWith: (overrides: Record<string, unknown>) => void;
  computeReadyWith: (overrides: Record<string, unknown>) => boolean;
  submitting: boolean;
  labelEl: React.ReactNode;
  helperEl: React.ReactNode;
  deferredNotesField?: {
    field: FieldSchema;
    value: unknown;
    setValue: (key: string, next: unknown) => void;
  };
}) {
  const [pendingDestructive, setPendingDestructive] = useState<string | null>(null);
  // Explicit `x-destructive` only — no position-based default. The substrate
  // owns which choice (if any) requires inline confirmation.
  const destructiveValue = field.xDestructive ?? null;

  const handleClick = (option: string) => {
    const isDestructive = option === destructiveValue;
    if (isDestructive) {
      // Don't set value yet — defer until confirm so a cancel leaves
      // the prior selection (or none) intact.
      setPendingDestructive(option);
      return;
    }
    setValue(field.key, option);
    if (computeReadyWith({ [field.key]: option })) {
      submitWith({ [field.key]: option });
    }
  };

  const confirmDestructive = () => {
    if (pendingDestructive === null) return;
    setValue(field.key, pendingDestructive);
    if (computeReadyWith({ [field.key]: pendingDestructive })) {
      submitWith({ [field.key]: pendingDestructive });
    }
    setPendingDestructive(null);
  };

  const cancelDestructive = () => {
    setPendingDestructive(null);
    // Notes was specifically for the rejection — clear if the user backs out.
    if (deferredNotesField) {
      deferredNotesField.setValue(deferredNotesField.field.key, "");
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {labelEl}
      <div className="flex gap-3 w-full" data-testid={`enum-pills-${field.key}`}>
        {options.map((opt, idx) => (
          <button
            key={opt}
            type="button"
            disabled={submitting}
            onClick={() => handleClick(opt)}
            className={getEnumPillClass(opt, idx, destructiveValue)}
            data-testid={`enum-pill-${field.key}-${opt.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {getEnumOptionLabel(field, options, opt)}
          </button>
        ))}
      </div>
      {helperEl}

      {/* Inline destructive confirmation strip — hosts the deferred notes
          textarea so users can explain what was wrong before submit. */}
      <Collapse in={pendingDestructive !== null} unmountOnExit timeout={250}>
        <div className="border border-primary-error-300 bg-primary-error-50 rounded-md px-3 py-2 flex flex-col gap-3">
          <p className="text-xs text-primary-error-700">
            Are you sure you want to{" "}
            <strong className="text-primary-error-900">
              {pendingDestructive
                ? getEnumOptionLabel(field, options, pendingDestructive)
                : ""}
            </strong>
            ? This action cannot be undone.
          </p>
          {deferredNotesField && (
            <div className="flex flex-col gap-1">
              <label className="text-[11px] uppercase tracking-wide text-primary-error-700 font-semibold">
                {deferredNotesField.field.label}{" "}
                <span className="normal-case text-primary-error-500">
                  (optional — what was wrong?)
                </span>
              </label>
              <textarea
                rows={2}
                value={
                  deferredNotesField.value === undefined || deferredNotesField.value === null
                    ? ""
                    : String(deferredNotesField.value)
                }
                onChange={(e) =>
                  deferredNotesField.setValue(deferredNotesField.field.key, e.target.value)
                }
                disabled={submitting}
                placeholder={deferredNotesField.field.description ?? ""}
                className="rounded-md border border-primary-error-300 bg-base-white px-3 py-2 text-sm focus:outline-none focus:border-primary-error-500 focus:ring-1 focus:ring-primary-error-500 disabled:opacity-50 resize-none"
              />
            </div>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={submitting}
              onClick={confirmDestructive}
              className="btn-error-primary text-xs font-semibold px-3 py-1.5"
              data-testid={`enum-pill-${field.key}-confirm`}
            >
              {submitting
                ? "Submitting…"
                : `Yes, ${
                    pendingDestructive
                      ? getEnumOptionLabel(field, options, pendingDestructive)
                      : ""
                  }`}
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={cancelDestructive}
              className="btn-secondary-gray text-xs font-semibold !px-3 !py-1.5"
              data-testid={`enum-pill-${field.key}-cancel`}
            >
              Cancel
            </button>
          </div>
        </div>
      </Collapse>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ArrayOfObjectsField
// ---------------------------------------------------------------------------
// Per-row card editor for `array_of_objects`. Each item is rendered as a
// labeled card containing one FieldRow per sub-field, so any sub-field type
// (string / multiline / enum / number / boolean) renders with its proper
// control — driven entirely by the schema, no per-workflow special-casing.
//
// State model:
//   - Local `items` is the source of truth for what's rendered, including
//     in-progress empty rows (so "+ Add" actually adds a visible row even
//     before the user types anything).
//   - The cleaned array (empty rows filtered out) is propagated to the
//     parent on every change — that's what flows into the trigger payload.
//   - External resets (parent clears the form, switches workflows, prefill
//     arrives) re-sync local items via the `value`-changed effect, gated
//     on `lastEmittedRef` so self-driven updates don't trigger a re-sync.

function isItemNonEmpty(item: Record<string, unknown>): boolean {
  return Object.values(item).some(
    (v) =>
      v !== "" &&
      v !== null &&
      v !== undefined &&
      !(Array.isArray(v) && v.length === 0),
  );
}

function ArrayOfObjectsField({
  field,
  value,
  setValue,
  readOnly,
  inputBorderStyle,
  labelEl,
  helperEl,
  itemFieldOverrides,
  overrideKey,
  fieldConfidence,
  fieldLoading,
  fieldHidden,
  fieldErrors,
  fieldNotes,
  hideDescription = false,
}: {
  field: FieldSchema;
  value: unknown;
  setValue: (key: string, next: unknown) => void;
  readOnly: boolean;
  inputBorderStyle: React.CSSProperties | undefined;
  labelEl: React.ReactNode;
  helperEl: React.ReactNode;
  /** Stable schema key for every per-field map lookup — see FieldRow's prop. */
  overrideKey?: string;
  fieldConfidence?: Record<string, ConfidenceLevel>;
  fieldLoading?: Record<string, boolean>;
  fieldHidden?: Record<string, boolean>;
  fieldErrors?: Record<string, string | null | undefined>;
  fieldNotes?: Record<string, string>;
  hideDescription?: boolean;
  itemFieldOverrides?: Record<
    string,
    (args: {
      field: FieldSchema;
      value: unknown;
      /** Writes any sub-key of THIS row — pass the sub-field key explicitly.
       *  (Unlike the default renderer's bound setter, the key is honored, so
       *  one override can drive a composite control spanning two keys.) */
      setValue: (key: string, next: unknown) => void;
      readOnly: boolean;
      /** Row index within the array — for per-row DOM ids/testids. */
      index: number;
      /** The row's full current item object (read-only view). */
      item: Record<string, unknown>;
    }) => React.ReactNode
  >;
}) {
  // The array's stable schema key — `field.key` is synthetic for a NESTED
  // array (see FieldRow's `overrideKey` doc), and every map below is keyed by
  // the schema name.
  const mapKey = overrideKey ?? field.key;
  const subFields = (field.itemFields ?? []).filter(
    // A sub-field hides on EITHER address: its bare schema name (how the
    // add-in's mortgagee form has always keyed `fieldHidden`, and the keys did
    // not change when the fields moved one level down) or the dotted
    // "<arrayKey>.<subKey>" form the renderer already uses for
    // `itemFieldOverrides`. Hiding never deletes the value — same contract as
    // the top-level filter.
    (sub) => !fieldHidden?.[sub.key] && !fieldHidden?.[`${mapKey}.${sub.key}`],
  );

  // Initialize from incoming value, but always show at least one row so
  // there's something to type into.
  const [items, setItems] = useState<Record<string, unknown>[]>(() => {
    const fromValue = Array.isArray(value)
      ? (value as Record<string, unknown>[])
      : [];
    return fromValue.length > 0 ? fromValue : [{}];
  });

  // Track the last cleaned array we emitted so we can distinguish parent-
  // driven resets (re-sync local state) from echoes of our own emit.
  const lastEmittedRef = useRef<string>(
    JSON.stringify(Array.isArray(value) ? value : []),
  );

  useEffect(() => {
    const incoming = JSON.stringify(Array.isArray(value) ? value : []);
    if (incoming !== lastEmittedRef.current) {
      const fromValue = Array.isArray(value)
        ? (value as Record<string, unknown>[])
        : [];
      setItems(fromValue.length > 0 ? fromValue : [{}]);
      lastEmittedRef.current = incoming;
    }
  }, [value]);

  function emit(nextItems: Record<string, unknown>[]) {
    setItems(nextItems);
    const cleaned = nextItems.filter(isItemNonEmpty);
    lastEmittedRef.current = JSON.stringify(cleaned);
    setValue(field.key, cleaned);
  }

  function updateSubField(idx: number, subKey: string, next: unknown) {
    emit(items.map((it, i) => (i === idx ? { ...it, [subKey]: next } : it)));
  }

  function addItem() {
    emit([...items, {}]);
  }

  function removeItem(idx: number) {
    const next = items.filter((_, i) => i !== idx);
    // Always keep at least one visible row so the field doesn't disappear.
    emit(next.length > 0 ? next : [{}]);
  }

  // Singular form for the "+ Add" button label — strip a trailing "s" off
  // the array's plural label as a best-effort heuristic ("Mortgagees" →
  // "Mortgagee"). Falls back to the full label when no trailing "s".
  const singular = field.label.endsWith("s")
    ? field.label.slice(0, -1)
    : field.label;

  return (
    <div className="flex flex-col gap-2" style={inputBorderStyle}>
      {labelEl}
      {helperEl}
      <div className="flex flex-col gap-3">
        {items.map((item, idx) => (
          <div
            key={idx}
            className="rounded-lg border border-primary-gray-light-200 bg-primary-gray-light-50 p-3 flex flex-col gap-3"
            data-testid={`array-row-${field.key}-${idx}`}
          >
            <div className="flex items-center justify-between">
              <span className="text-2xs font-semibold text-primary-gray-light-500 uppercase tracking-wide">
                {field.xItemLabel
                  ? `${field.xItemLabel}${items.length > 1 ? ` ${idx + 1}` : ""}`
                  : `${singular} ${idx + 1}`}
              </span>
              {items.length > 1 && !readOnly && (
                <button
                  type="button"
                  onClick={() => removeItem(idx)}
                  className="text-2xs font-semibold text-primary-error-700 hover:underline"
                  data-testid={`array-remove-${field.key}-${idx}`}
                >
                  Remove
                </button>
              )}
            </div>
            {subFields.map((sub) => {
              // Sub-field override — keyed "<arrayKey>.<subKey>". Receives
              // the ORIGINAL sub schema (stable key across rows); writes go
              // through the same per-row updateSubField as the default path.
              const subOverride = itemFieldOverrides?.[`${mapKey}.${sub.key}`];
              // A moved field keeps its decorations: the analyze_email
              // confidence chip (and its coloured left border) and the prefill
              // spinner. The producer re-keys confidence to the dotted
              // sub-field address; loading falls back to the PARENT array's
              // flag, because the add-in derives `fieldLoading` from the
              // schema's TOP-LEVEL property names — so `actions: true` during
              // prefill is exactly today's "every field is loading" and needs
              // no producer change.
              const subConfidence = fieldConfidence?.[`${mapKey}.${sub.key}`];
              const subLoading =
                fieldLoading?.[`${mapKey}.${sub.key}`] ?? fieldLoading?.[mapKey];
              const subError =
                fieldErrors?.[`${mapKey}.${sub.key}`] ?? fieldErrors?.[sub.key];
              const subNote =
                fieldNotes?.[`${mapKey}.${sub.key}`] ?? fieldNotes?.[sub.key];
              if (subOverride) {
                return (
                  <Fragment key={sub.key}>
                    {subOverride({
                      field: sub,
                      value: item[sub.key],
                      // Key-honoring on purpose: a composite override (e.g.
                      // State+ZIP on one line) writes BOTH its keys through
                      // this one setter.
                      setValue: (subKey, next) =>
                        updateSubField(idx, subKey, next),
                      readOnly,
                      index: idx,
                      item,
                    })}
                  </Fragment>
                );
              }
              // Synthesize a unique key so DOM ids and label htmlFor refs
              // don't collide across rows. The sub-field's setValue callback
              // closure-captures sub.key for state updates, so the
              // synthetic key only affects DOM identity.
              const rowSub: FieldSchema = {
                ...sub,
                key: `${field.key}-${idx}-${sub.key}`,
              };
              return (
                <div key={sub.key} className="flex flex-col gap-1">
                  <FieldRow
                    field={rowSub}
                    value={item[sub.key]}
                    setValue={(_synthKey, next) =>
                      updateSubField(idx, sub.key, next)
                    }
                    readOnly={readOnly}
                    confidence={subConfidence}
                    loading={subLoading}
                    note={subNote}
                    hideDescription={hideDescription}
                    // The ORIGINAL sub key, so a sub-field that is ITSELF an
                    // array (mortgagees inside actions) keeps matching its own
                    // "mortgagees.mailing_state" overrides rather than the
                    // synthetic per-row key above.
                    overrideKey={sub.key}
                    itemFieldOverrides={itemFieldOverrides}
                    fieldConfidence={fieldConfidence}
                    fieldLoading={fieldLoading}
                    fieldHidden={fieldHidden}
                    fieldErrors={fieldErrors}
                    fieldNotes={fieldNotes}
                  />
                  {subError && (
                    <span
                      className="text-xs text-primary-error-700"
                      role="alert"
                      data-testid={`field-error-${sub.key}`}
                    >
                      {subError}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      {!readOnly && !field.xNoAdd && (
        <button
          type="button"
          onClick={addItem}
          className="self-start text-xs font-semibold text-primary-gray-light-700 hover:underline mt-1"
          data-testid={`array-add-${field.key}`}
        >
          + Add {singular.toLowerCase()}
        </button>
      )}
    </div>
  );
}

function FallbackJson({
  text,
  onChange,
  error,
  disabled,
}: {
  text: string;
  onChange: (text: string) => void;
  error: string | null;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="rounded-lg bg-primary-warning-50 border border-primary-warning-200 p-3 text-xs text-primary-warning-700">
        This step&apos;s input shape isn&apos;t fully supported by the form renderer.
        You can still complete it by entering JSON below.
      </div>
      <textarea
        value={text}
        onChange={(e) => onChange(e.target.value)}
        rows={8}
        disabled={disabled}
        className="rounded-lg border border-primary-gray-light-300 px-3 py-2 text-sm bg-base-white text-primary-gray-light-900 font-mono disabled:bg-primary-gray-light-50 disabled:text-primary-gray-light-700"
        aria-label="JSON input"
      />
      {error && (
        <span className="text-xs text-primary-error-700" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
